import { IconButton, Text } from "@carbon6/c6-frontend-design";
import { useEffect, type ReactNode } from "react";
import { hatcheryTextModes } from "@/app/theme";
import { formatBytes } from "@/utils";
import { useDatasetDownload } from "./useDatasetDownload";
import styles from "./DatasetDownload.module.css";

// 실패 목록을 모두 나열하지 않는다. 수천 건이 실패해도 화면이 정상 동작해야 한다.
const VISIBLE_FAILURES = 5;

/**
 * 다운로드 진행 패널. 데이터셋 상세 스코프에서 실행과 표시를 함께 소유한다.
 *
 * 트리거(헤더·트리)와 형제이므로 요청은 `datasetDownloadStore` 로 받는다. 페이지를 벗어나면 훅이
 * 다운로드를 중단한다(상태를 유지할 전역 저장소가 없다). 그래서 진행 중에는 그 사실을 화면에 표시한다.
 */
export function DatasetDownloadPanel({ datasetUuid }: { datasetUuid: string }): ReactNode {
  const controller = useDatasetDownload(datasetUuid);

  // 탭 닫기·새로고침만 막을 수 있다. 라우터가 data router 가 아니므로 앱 내 이동은 가로챌 수 없다.
  // 그래서 훅이 언마운트 시 중단하고, 문구로 알린다.
  useEffect(() => {
    if (!controller.active) return undefined;
    const handler = (event: BeforeUnloadEvent): void => {
      event.preventDefault();
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [controller.active]);

  if (controller.phase === "idle") return null;

  const { progress, failures, conflicts } = controller;
  const settled = progress.completed + progress.skipped + progress.failed;
  const percent = progress.total === 0 ? 0 : Math.round((settled / progress.total) * 100);

  return (
    <section className={styles.panel} aria-label="파일 다운로드 진행">
      <div className={styles.panelHeader}>
        <Text variant={hatcheryTextModes["body-bold"]}>{headline(controller)}</Text>
        <div className={styles.panelActions}>
          {controller.active ? (
            <IconButton text="중단" size="sm" variant="ghost" onClick={controller.cancel} />
          ) : null}
          {!controller.active && failures.length > 0 ? (
            <IconButton
              text={`실패 ${failures.length}건 재시도`}
              size="sm"
              variant="surface"
              onClick={controller.retryFailed}
            />
          ) : null}
          {!controller.active ? (
            <IconButton text="닫기" size="sm" variant="ghost" onClick={controller.dismiss} />
          ) : null}
        </div>
      </div>

      <div
        className={styles.progressTrack}
        role="progressbar"
        aria-valuenow={percent}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div className={styles.progressFill} style={{ width: `${percent}%` }} />
      </div>

      <div className={styles.panelBody}>
        <Text variant={hatcheryTextModes["body-normal"]}>
          {progress.total.toLocaleString()}개 중 {settled.toLocaleString()}개 처리 · 받음{" "}
          {progress.completed.toLocaleString()} · 건너뜀 {progress.skipped.toLocaleString()} · 실패{" "}
          {progress.failed.toLocaleString()}
          {controller.totalBytes > 0 ? ` · 총 ${formatBytes(controller.totalBytes)}` : ""}
        </Text>

        {controller.active ? (
          <Text variant={hatcheryTextModes["body-normal"]} className={styles.note}>
            이 페이지를 벗어나면 다운로드가 중단됩니다. 다시 실행하면 남은 파일부터 이어받습니다.
          </Text>
        ) : null}

        {controller.errorMessage === null ? null : (
          <Text variant={hatcheryTextModes["body-normal"]} className={styles.error}>
            {controller.errorMessage}
          </Text>
        )}

        {conflicts.length === 0 ? null : (
          <Text variant={hatcheryTextModes["body-normal"]} className={styles.note}>
            경로가 겹쳐 {conflicts.length}개를 건너뛰었습니다. 덮어쓰지 않기 위해 먼저 만난 파일만
            받았습니다.
          </Text>
        )}

        {failures.length === 0 ? null : (
          <ul className={styles.failureList}>
            {failures.slice(0, VISIBLE_FAILURES).map((failure) => (
              <li key={failure.entry.uuid} title={failure.message}>
                {failure.entry.relativePath}
              </li>
            ))}
            {failures.length > VISIBLE_FAILURES ? (
              <li>외 {failures.length - VISIBLE_FAILURES}건</li>
            ) : null}
          </ul>
        )}
      </div>
    </section>
  );
}

function headline(controller: ReturnType<typeof useDatasetDownload>): string {
  if (controller.phase === "collecting") return "다운로드 대상을 확인하고 있습니다.";
  if (controller.phase === "error") return "다운로드를 시작하지 못했습니다.";
  if (controller.phase === "downloading") return "파일을 내려받고 있습니다.";
  if (controller.cancelled) return "다운로드를 중단했습니다.";
  if (controller.progress.failed > 0) return "일부 파일을 받지 못했습니다.";
  return "다운로드를 마쳤습니다.";
}
